import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Settings, Globe, Shield, BarChart3, Users, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function TolokyAdminPanel() {
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  useEffect(() => {
    Promise.all([fetchOrgSettings(), fetchUsageReport()]).finally(() => setLoading(false));
  }, []);

  const fetchOrgSettings = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/admin/settings`, {
        headers: getHeaders(), withCredentials: true,
      });
      setSettings(res.data);
    } catch {}
  };

  const fetchUsageReport = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/admin/usage-report`, {
        headers: getHeaders(), withCredentials: true,
      });
      setUsage(res.data.records || []);
    } catch {}
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/comms/toloky/admin/settings`, settings, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Toloky settings updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Globe className="w-5 h-5 text-violet-500" />
          Toloky Admin Configuration
        </h2>
        <p className="text-sm text-slate-500">
          Manage interpreter settings across your organization
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none border-b border-slate-200 w-full justify-start bg-transparent">
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-500">
            <Settings className="w-3.5 h-3.5 mr-1.5" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="usage" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-violet-500">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
            Usage Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4">
          {settings && (
            <div className="bg-white border border-slate-200 p-6 space-y-6">
              {/* Toggle */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div>
                  <p className="text-sm font-medium text-slate-800">Enable Toloky</p>
                  <p className="text-xs text-slate-500">Organization-wide toggle</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                />
              </div>

              {/* Voice Simulation Policy */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-600">Voice Simulation Policy</Label>
                <Select
                  value={settings.voice_simulation_default}
                  onValueChange={(v) => setSettings({ ...settings, voice_simulation_default: v })}
                >
                  <SelectTrigger className="w-full rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none">
                    <SelectItem value="require_consent">Require Consent</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="enabled_by_default">Enabled by Default</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Usage Limit */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-600">
                  Monthly Minutes Limit per User
                </Label>
                <Input
                  type="number"
                  value={settings.monthly_minutes_limit}
                  onChange={(e) => setSettings({ ...settings, monthly_minutes_limit: parseInt(e.target.value) || 0 })}
                  className="w-48 rounded-none"
                />
              </div>

              {/* Notifications */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">Notify Participants</p>
                  <p className="text-xs text-slate-500">
                    Alert all meeting participants when Toloky is active
                  </p>
                </div>
                <Switch
                  checked={settings.notify_participants}
                  onCheckedChange={(v) => setSettings({ ...settings, notify_participants: v })}
                />
              </div>

              {/* Default Voices */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Female Default</Label>
                  <Input
                    value={settings.default_voice_female || "ava"}
                    onChange={(e) => setSettings({ ...settings, default_voice_female: e.target.value })}
                    className="rounded-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Male Default</Label>
                  <Input
                    value={settings.default_voice_male || "andrew"}
                    onChange={(e) => setSettings({ ...settings, default_voice_male: e.target.value })}
                    className="rounded-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Neutral Default</Label>
                  <Input
                    value={settings.default_voice_neutral || "fable"}
                    onChange={(e) => setSettings({ ...settings, default_voice_neutral: e.target.value })}
                    className="rounded-none"
                  />
                </div>
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 rounded-none"
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="usage" className="mt-4">
          <div className="bg-white border border-slate-200 p-6">
            {usage.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <BarChart3 className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No usage data yet</p>
              </div>
            ) : (
              <ScrollArea className="max-h-96">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">User</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">Duration</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">Source</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">Target</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">Voice Sim</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.map((r, i) => (
                      <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 text-slate-700 font-mono">{r.user_id?.slice(0, 12)}...</td>
                        <td className="py-2 px-2 text-slate-700">{Math.round(r.duration_seconds / 60)}m</td>
                        <td className="py-2 px-2 text-slate-700">{r.source_language || "-"}</td>
                        <td className="py-2 px-2 text-slate-700">{r.target_language || "-"}</td>
                        <td className="py-2 px-2">{r.voice_simulation_used ? "Yes" : "No"}</td>
                        <td className="py-2 px-2 text-slate-500">{r.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
